import React, { ChangeEvent, ForwardedRef, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cropper } from '@components/cropper';
import { Props } from '@components/drag-and-drop/drag-and-drop.type';
import { getImagesList } from '@components/drag-and-drop/utils/get-images-list';
import { IconCross } from '@components/icons/icon-cross';
import { IconImage } from '@components/icons/icon-image';
import { IconPencil } from '@components/icons/icon-pencil';
import { OptimizedImage } from '@components/optimized-image';
import { FILE_TYPE_PNG } from '@constants/global';
import { useToast } from '@hooks/use-toast/use-toast';
import { getAcceptedFormatForInputFile } from '@utils/get-accepted-format-for-imput-file';
import { isFileFormatCorrect } from '@utils/is-file-format-correct';
import { isLargePng } from '@utils/is-large-png';
import classNames from 'classnames';

// @see https://levelup.gitconnected.com/crop-images-on-upload-in-your-react-app-with-react-image-crop-5f3cd0ad2b35
// @see https://codesandbox.io/s/y09komm059?file=/src/canvasUtils.js
export const DragAndDrop = forwardRef(
  (
    {
      translations,
      allowedTypes,
      onChange,
      value,
      disableRemoveImage,
      disableCropperModal = false,
      removeLoadImageButton,
      imageWidth,
      imageFormat,
      breakpoints = {},
      disabled,
      hideHelper = false,
      buttonEditClassName = 'bg-tertiary p-2',
      absolutePositionButtonChangeImageColorClassName = 'absolute bottom-2 right-2',
      iconSizeClassName = 'size-5',
      dragAndDropHeight = 'h-[280px]',
      isSquareImage = false,
    }: Props,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref: ForwardedRef<HTMLImageElement>
  ) => {
    const { t } = useTranslation();
    const toast = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [src, setSrc] = useState(value || null);
    const [tempSrc, setTempSrc] = useState<string | null>(null);
    const [tempFile, setTempFile] = useState<File | null>(null);
    const inputFile = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (!file) {
        setSrc(value ?? null);
      }
    }, [value, file]);

    const acceptedFormat = useMemo(
      () => getAcceptedFormatForInputFile({ acceptedFormat: allowedTypes }),
      [allowedTypes]
    );

    const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
      const MAX_FILE_SIZE = 1024 * 1024 * 10;
      const files = e.target.files;

      if (!files?.[0]) return;

      if (!isFileFormatCorrect({ type: files[0].type, allowedTypes })) {
        e.preventDefault();
        toast.push({
          title: t('errors:fileTypeIncorrectTitle'),
          text: t('errors:fileTypeIncorrectBody'),
          type: 'error',
          disableTimeOut: true,
        });
        return;
      }
      if (files[0].size > MAX_FILE_SIZE) {
        e.preventDefault();
        toast.push({
          title: t('errors:fileSizeIncorrectTitle'),
          text: t('errors:fileSizeIncorrectBody'),
          type: 'error',
          disableTimeOut: true,
        });
        return;
      }

      const imagesList = getImagesList(files);
      if (!imagesList.length) {
        return;
      }
      const { file: imageFile, src: imageSrc } = imagesList[0];
      // An unreadable PNG keeps getting the PNG toast, as it did before the util rejected on it.
      const isBiggerThatAccepted = await isLargePng(files[0].type, imagesList[0]).catch(() => true);
      if (isBiggerThatAccepted) {
        toast.push({
          title: t('errors:pngFileSizeIncorrectTitle'),
          text: t('errors:pngFileSizeIncorrectBody'),
          type: 'error',
          disableTimeOut: true,
        });
        return;
      }
      if (hideHelper) {
        setTempFile(file);
        setTempSrc(src);
      }
      setFile(imageFile);
      setSrc(imageSrc);

      if (disableCropperModal) {
        onChange([imagesList[0]]);
      } else setIsOpen(true);
    };
    const handleCropCancel = () => {
      if (hideHelper) {
        setFile(tempFile);
        setSrc(tempSrc);
      } else {
        setFile(null);
        setSrc(null);
      }
      setIsOpen(false);
    };

    const handleCropSave = (croppedFile: File, meta: { aspectRatio: number }) => {
      const isSquare = Math.abs(meta.aspectRatio - 1) < 0.01;
      const imageSrc = URL.createObjectURL(croppedFile);
      setFile(croppedFile);
      setSrc(imageSrc);
      onChange([{ file: croppedFile, src: imageSrc, isSquare }]);
      setIsOpen(false);
    };

    const handleChangeImage = () => {
      if (disabled) return;
      if (disableRemoveImage && inputFile.current) inputFile.current.click();
      else {
        setFile(null);
        setSrc(null);
        onChange([]);
      }
    };

    const cropperImage: { file: File; src: string } | null = file ? { file, src: src ?? '' } : null;

    const calculateFormat = () => {
      if (imageFormat && imageWidth) return imageFormat;
      if (!imageFormat && imageWidth) return FILE_TYPE_PNG;
      return undefined;
    };

    return (
      <div>
        {cropperImage && (
          <Cropper
            open={isOpen}
            image={cropperImage}
            aspectRatios={[{ value: 1, isDefault: true }, { value: 16 / 9 }]}
            onCancel={handleCropCancel}
            onSave={handleCropSave}
            translations={{
              save: translations.save,
              cancel: translations.cancel,
              zoom: translations.zoom,
              rotate: translations.rotate,
              aspectRatioToggleLabel: t('eventInfo:coverAspectRatioTitle'),
              aspectRatioToggleSublabel: t('eventInfo:coverAspectRatioDescription'),
            }}
          />
        )}

        <div className={classNames('relative p-4 bg-tertiary rounded-md', dragAndDropHeight)}>
          {!!src && (
            <div className='w-full h-full flex items-center justify-center'>
              <div className='relative h-full'>
                <OptimizedImage
                  className={classNames('object-contain h-full w-auto max-h-full', {
                    'aspect-square': isSquareImage,
                  })}
                  src={src}
                  alt='Cover event'
                  width={imageWidth}
                  format={calculateFormat()}
                  fluid={!isSquareImage || disableCropperModal}
                  loading='eager'
                  breakpoints={breakpoints}
                />
                {!disabled && (
                  <div className={absolutePositionButtonChangeImageColorClassName}>
                    <input onChange={onFileChange} accept={acceptedFormat} ref={inputFile} type='file' hidden />
                    {!removeLoadImageButton && (
                      <button
                        data-testid='drag-and-drop--button-change-image'
                        type='button'
                        className={classNames(
                          'inline-flex items-center shadow-[-1px_2px_16px_0_rgba(0,0,0,0.15)] border border-soft rounded-md',
                          buttonEditClassName
                        )}
                        onClick={handleChangeImage}>
                        {disableRemoveImage && <IconPencil className={classNames('text-primary', iconSizeClassName)} />}
                        {!disableRemoveImage && <IconCross className={classNames('text-primary', iconSizeClassName)} />}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {!src && (
            <div className='h-full flex items-center justify-center'>
              <label
                htmlFor='drag-and-drop-image-input'
                className='relative h-full aspect-square flex flex-col items-center justify-center cursor-pointer rounded-md border border-dashed border-medium'>
                <input
                  id='drag-and-drop-image-input'
                  aria-label='drag and drop image'
                  className='absolute left-0 top-0 opacity-0 w-full h-full outline-hidden cursor-pointer'
                  type='file'
                  data-testid='drag-and-drop-image-input'
                  name='drag-and-drop-image-input'
                  onChange={onFileChange}
                  accept={acceptedFormat}
                />

                <div>
                  <IconImage className='h-9 w-auto text-subtitle-m text-primary my-0 mx-auto' />
                  <p className='text-center mt-5 text-subtitle-s text-primary'>{translations.title}</p>
                  <p className='text-center text-body-xs text-secondary'>{translations.subtitle}</p>
                </div>
              </label>
            </div>
          )}
        </div>
      </div>
    );
  }
);
